import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import {
  DATE_FORMAT_WEEK_DAY_MID_FNS,
  PeriodTypeEnum,
  TimetableStructurePeriod,
  TimetableStructureTimeSlot,
  WeekdayEnum,
} from '@schooly/api';
import { buildTestId, TIMETABLE_STRUCTURE } from '@schooly/constants/test-ids';
import {
  Grid,
  GridBody,
  GridCell,
  GridHead,
  GridRow,
  TWO_TONE_CLASSNAME,
  TwoToneBackground,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { FC, Fragment, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { AmPmDivider } from './AmPmDivider';
import { getPeriodTypePalette, getWeekdayLabel } from './utils';
import { resolveStructureRegisterSessions } from '../SchoolTimetables/register-session-display';

interface StructurePeriodsGridProps {
  weekdays: WeekdayEnum[];
  periods: TimetableStructurePeriod[];
  timeSlots: TimetableStructureTimeSlot[];
}

export const StructurePeriodsGrid: FC<StructurePeriodsGridProps> = ({
  weekdays,
  periods,
  timeSlots,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();

  const registerSessions = useMemo(() => resolveStructureRegisterSessions(periods), [periods]);

  const hasTimeSlot = useCallback(
    (periodId: string, weekday: WeekdayEnum): boolean => {
      return timeSlots.some((slot) => slot.period_id === periodId && slot.weekday === weekday);
    },
    [timeSlots],
  );

  return (
    <Grid>
      <GridHead sx={{ '&.MuiTableHead-root .MuiTableCell-root': { height: 30, p: 0.5 } }}>
        <GridRow>
          <GridCell width={135} />
          <GridCell />
          {weekdays.map((weekday) => (
            <GridCell
              key={weekday}
              sx={{ minWidth: 50, width: `calc(60% / ${weekdays.length})` }}
              center
            >
              <Typography variant="h3" color="text.primary">
                {getWeekdayLabel(weekday, DATE_FORMAT_WEEK_DAY_MID_FNS).toUpperCase()}
              </Typography>
            </GridCell>
          ))}
        </GridRow>
      </GridHead>

      <GridBody>
        {periods.map((period) => {
          const session = registerSessions.get(period.id) ?? null;
          const palette = getPeriodTypePalette(period.type, theme);
          const secondaryPalette =
            session &&
            period.type === PeriodTypeEnum.Lesson &&
            getPeriodTypePalette(PeriodTypeEnum.Registration, theme);

          const tooltipText = (() => {
            const name = `"${period.name}"`;
            const typeLabel = formatMessage({
              id: `school-timetableStructure-periodType-${period.type}`,
            });
            if (!session) return `${typeLabel} ${name}`;
            const sessionLabel = formatMessage({
              id: `school-timetableStructure-registerSession-${session}`,
            });
            return period.type === PeriodTypeEnum.Lesson
              ? `${typeLabel} ${formatMessage({ id: 'school-timetableStructure-and' })} ${sessionLabel} ${name}`
              : `${sessionLabel} ${name}`;
          })();

          return (
            <Fragment key={period.id}>
              {session && <AmPmDivider session={session} />}
              <GridRow
                withoutDefaultHover
                data-test-id={buildTestId(
                  TIMETABLE_STRUCTURE.GRID_ROW_PREFIX,
                  { name: period.name },
                  'name',
                )}
              >
                <GridCell borderBottom={false} py={0.5}>
                  <TypographyWithOverflowHint variant="h3" color="text.primary">
                    {period.start_time} - {period.end_time}
                  </TypographyWithOverflowHint>
                </GridCell>

                <GridCell borderBottom={false} py={0.5} sx={{ maxWidth: 150 }}>
                  <TypographyWithOverflowHint variant="h3" color="text.primary" noWrap>
                    {period.name}
                  </TypographyWithOverflowHint>
                </GridCell>

                {weekdays.map((weekday) => {
                  const isActive = hasTimeSlot(period.id, weekday);
                  const weekdayLabel = getWeekdayLabel(
                    weekday,
                    DATE_FORMAT_WEEK_DAY_MID_FNS,
                  ).toUpperCase();

                  return (
                    <GridCell key={weekday} borderBottom={false} p={0.25} height={30}>
                      {isActive ? (
                        <Tooltip
                          describeChild
                          title={tooltipText}
                          componentsProps={{ tooltip: { sx: { minWidth: 200, p: 1.25 } } }}
                        >
                          <Box
                            data-test-id={buildTestId(
                              TIMETABLE_STRUCTURE.GRID_SLOT_PREFIX,
                              { name: weekdayLabel },
                              'name',
                            )}
                            sx={(theme) => ({
                              width: '100%',
                              height: '100%',
                              position: 'relative',
                              backgroundColor: palette.secondary,
                              borderRadius: theme.spacing(0.5),
                              overflow: 'hidden',
                              [TWO_TONE_CLASSNAME]: {
                                path: {
                                  fill: secondaryPalette ? secondaryPalette.secondary : undefined,
                                },
                              },
                              ':hover': {
                                backgroundColor: palette.primary,
                                [TWO_TONE_CLASSNAME]: {
                                  path: {
                                    fill: secondaryPalette ? secondaryPalette.primary : undefined,
                                  },
                                },
                              },
                            })}
                          >
                            {secondaryPalette && <TwoToneBackground />}
                          </Box>
                        </Tooltip>
                      ) : null}
                    </GridCell>
                  );
                })}
              </GridRow>
            </Fragment>
          );
        })}
      </GridBody>
    </Grid>
  );
};
